import {
  useCallback,
  useEffect,
  useState } from "react";
import {
  ZONES,
  releaseChecks,
  readySummary,
  storeLinks,
  adminScreen,
  duplicateBookTitles,
  bookIdOfAssetKey,
  dubRows,
  dubCount,
  isOnWeb,
  type ReadyItem,
  type KbPackDoc,
  type NpcAgentDoc,
  arTargetKey,
  arInteractIssues,
  arPagesGone,
  arPosterKey,
  goodsIdOfSlot,
  arSceneReady,
  stageJitsiOnly,
  testEventVisible,
  stuckObjects,
  brokenLinks,
  zoneSay,
  cardVideoUrl,
  guardianLines,
  hasEventSideDoor,
  kidsModePublished,
  landingAgeAndTagline,
  dueSay,
  daysUntil,
} from "@greg-chick/shared";
import { subscribeAdminUpdates } from "../lib/liveUpdates";
import {
  contentBase,
  getEvents,
  getNotices,
  getRallyEvent,
  loadGoodsDoc,
  loadManifest,
  getNpcAgents,
  getAiKb,
  loadZoneMap,
  listBooks,
  loadBookDoc,
  adminApi,
  getSiteText,
} from "../lib/contentApi";

/**
 * ✅ **출시 준비** — 아직 안 채운 자리를 한 곳에 모아 보여주고, 누르면 그 화면으로 보낸다.
 *
 * ## 왜 만들었나 (2026-08-21)
 * 안 채운 자리가 **여섯 화면에 흩어져** 있었다 — 굿즈 스토어 주소, AR 영상·포스터, 행사,
 * 랠리 신청 폼, 문의 폼, 체험공간 링크. 운영자가 "뭘 더 해야 하나"를 알 방법이 없어서
 * 개발자가 표로 알려주고 있었다. 그건 오래 못 간다.
 *
 * ## 여기서 판정하지 않는다
 * 무엇이 비었는지·어느 화면에서 채우는지는 shared `releaseChecks()` 가 정한다.
 * 이 파일은 **모아 오고 그리는 일**만 한다 — 화면이 따로 세면 나중에 배포 점검과 갈린다.
 *
 * ## 못 불러오는 것은 조용히 넘긴다
 * 엣지가 꺼져 있거나 아직 안 만든 문서가 있을 수 있다. 그때 화면 전체를 막으면 안 된다 —
 * 불러온 것만으로 센다(이건 "이 값이 없다"와 "0개다"를 구별할 수 없다는 뜻이라 아래에 적어 둔다).
 */
export function ReleaseReady({ onGo }: { onGo: (screen: string) => void }) {
  const [items, setItems] = useState<ReadyItem[] | null>(null);
  const [partial, setPartial] = useState(false);
  const [busy, setBusy] = useState(false);
  /** 마지막으로 센 시각 — "언제 기준인지" 가 안 보이면 목록을 믿기 어렵다. */
  const [at, setAt] = useState<Date | null>(null);

  /**
   * 다시 세는 때는 셋이다.
   * ① 화면에 들어올 때 ② **누가 게시하면 바로**(엣지 푸시) ③ 창으로 돌아왔을 때
   *
   * ②가 핵심이다. 이 화면을 열어 둔 채 다른 창에서 굿즈를 채워 올리면, 여기는 옛 숫자를
   * 그대로 보여준다 — 다 채웠는데 "6개 남음"이라고 적혀 있으면 그 목록을 못 믿게 된다.
   * ③은 푸시가 막혔을 때의 그물이다(엣지가 꺼져 있어도 창을 다시 보면 맞는 숫자가 된다).
   */
  const refresh = useCallback(() => {
    setBusy(true);
    return gather().then((r) => {
      setItems(releaseChecks(r.input));
      setPartial(r.partial);
      setAt(new Date());
      setBusy(false);
    });
  }, []);

  useEffect(() => {
    let alive = true;
    const run = () => {
      if (alive) void refresh();
    };
    run();
    const off = subscribeAdminUpdates(run); // ② 게시하면 바로
    const onFocus = () => {
      if (document.visibilityState === "visible") run();
    };
    window.addEventListener("focus", onFocus); // ③ 창으로 돌아왔을 때
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      alive = false;
      off();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [refresh]);

  if (items === null) return <p className="hint">살펴보는 중…</p>;

  return (
    <section className="ready">
      <div className="ready__head">
        <p className={`ready__sum${items.length === 0 ? " ready__sum--ok" : ""}`}>
          {readySummary(items)}
        </p>
        {/* 언제 기준인지 + 직접 다시 세는 버튼. 자동 갱신이 막혀도 운영자가 막히지 않게. */}
        <span className="ready__when">
          {busy ? "세는 중…" : at ? `${at.toLocaleTimeString("ko-KR")} 기준` : ""}
          <button type="button" className="btn btn--sm" onClick={() => void refresh()} disabled={busy}>
            다시 확인
          </button>
        </span>
      </div>
      <p className="hint">
        <b>「웹에 올리기」를 누른 것만</b> 셉니다 — 아이 화면에 실제로 나간 것 기준이에요.
        누군가 게시하면 이 목록도 바로 다시 세요.
      </p>
      {partial ? (
        <p className="hint">
          ⚠️ 일부 정보를 못 불러왔어요(서버 연결). 아래 목록이 전부가 아닐 수 있어요.
        </p>
      ) : null}

      <ul className="ready__list">
        {items.map((it) => {
          const scr = adminScreen(it.screen);
          return (
            <li key={it.id} className={`ready__item ready__item--${it.severity}`}>
              <span className="ready__tag">{it.severity === "must" ? "꼭 필요" : "있으면 좋음"}</span>
              <div className="ready__text">
                <b>
                  {it.label}
                  {it.due ? <DueTag due={it.due} /> : null}
                </b>
                <p>{it.effect}</p>
              </div>
              <button type="button" className="btn btn--sm" onClick={() => onGo(it.screen)}>
                {scr ? `${scr.icon} ${scr.label}` : "채우러 가기"} →
              </button>
            </li>
          );
        })}
      </ul>

      {items.length === 0 ? (
        <p className="hint">
          새 굿즈나 AR 카드를 만들면 여기에 다시 나타나요. 가끔 들러서 확인해 주세요.
        </p>
      ) : null}
    </section>
  );
}

/** 여러 문서를 한 번에 읽어 판정에 넣을 숫자로 바꾼다. 하나가 실패해도 나머지로 센다. */
async function gather() {
  const [goods, manifest, scenes, events, rally, notices, maps, agents, kb, books, ui, siteText, lobby] =
    await Promise.allSettled([
    loadGoodsDoc(),
    loadManifest(),
    fetch(`${contentBase()}/content/ar-scenes.json`, { cache: "no-cache" }).then((r) =>
      r.ok ? r.json() : [],
    ),
    getEvents(),
    getRallyEvent(),
    getNotices(),
    Promise.all(ZONES.map((z) => loadZoneMap(z))),
    getNpcAgents(),
    getAiKb(),
    // 웹과 같은 목록(게시본+저장소를 합친 것) — 아이가 보는 책방 그대로를 센다.
    // 읽어주기까지 세려면 **본문**이 있어야 한다(자막이 있는 쪽만 셈에 든다).
    listBooks().then(async (list) => ({
      list,
      docs: await Promise.all(
        list.map((b) => loadBookDoc(b.id).then((d) => ({ id: b.id, doc: d })).catch(() => null)),
      ),
    })),
    // ── 🧑‍💼 `pnpm check:owner` 와 같은 사람 일 — 아이 화면이 읽는 그 게시본을 묻는다 ──
    // 🧸 영유아 모드: 게시본이 없으면(404) 코드 기본값이 아니라 「게시 안 됨」이다(check:owner 와 같다)
    fetch(`${adminApi()}/api/ui-features`, { cache: "no-store" }).then(async (r) => {
      if (r.status === 404) return null;
      if (!r.ok) throw new Error(String(r.status));
      return (await r.json()) as { mode?: unknown };
    }),
    getSiteText(),
    /**
     * 🚪 옆문은 **게시본 마을**에서 본다. `loadZoneMap` 은 엣지가 안 되면 저장소 지도로 넘어가는데,
     * 저장소엔 옆문이 이미 있어서 「있다」로 속는다. 게시본이 아예 없으면(404) 아이도 저장소 지도를 받으니 그걸로 본다.
     */
    fetch(`${adminApi()}/api/map/lobby`, { cache: "no-store" }).then(async (r) => {
      if (r.status === 404) {
        const repo = await fetch(`${contentBase()}/maps/lobby.json`, { cache: "no-cache" });
        if (!repo.ok) throw new Error(String(repo.status));
        return (await repo.json()) as unknown;
      }
      if (!r.ok) throw new Error(String(r.status));
      return (await r.json()) as unknown;
    }),
  ]);
  const partial = [goods, manifest, scenes, events, rally, notices, maps, agents, kb, books, ui, siteText, lobby].some(
    (r) => r.status === "rejected",
  );

  const products = goods.status === "fulfilled" ? goods.value.products : [];
  // 🐱 도슨트 — 못 읽으면 빈 문서로 센다(0 이면 항목이 안 뜬다). 없는 걸 있다고 우기지 않는다.
  const agentDoc: NpcAgentDoc = agents.status === "fulfilled" ? agents.value : { agents: [] };
  const kbDoc: KbPackDoc = kb.status === "fulfilled" ? kb.value : { packs: [] };
  const assets = manifest.status === "fulfilled" ? manifest.value.assets : {};
  // 🏠 운영자가 정해 둔 기본 회의 주소 — 이게 있으면 행사가 비어 있어도 열린다
  const siteMeet = notices.status === "fulfilled" ? notices.value.defaultMeetUrl : null;

  /** 카드 id → 저장소에 적힌 옛 「영상으로 보기」 주소(`fallback_youtube`) */
  const sceneLegacy = new Map<string, unknown>();
  if (scenes.status === "fulfilled") {
    const raw = scenes.value as unknown;
    const list = Array.isArray(raw) ? raw : ((raw as { scenes?: unknown[] })?.scenes ?? []);
    for (const s of list) {
      if (!s || typeof s !== "object") continue;
      const id = String((s as { id?: unknown }).id ?? "");
      if (id) sceneLegacy.set(id, (s as { fallback_youtube?: unknown }).fallback_youtube);
    }
  }
  const sceneIds = [...sceneLegacy.keys()];

  /**
   * 🔴 판정은 아이 화면·`check:owner` 와 같은 `cardVideoUrl` — 게시본 칸이 먼저, 없으면 저장소의 옛 칸.
   * 예전엔 게시본 칸만 봐서, 저장소에 주소가 있는 카드도 「비었다」로 셀 수 있었다(2026-10-09).
   */
  const missingVideo = sceneIds.filter(
    (id) => !cardVideoUrl(assets[arTargetKey(id)], sceneLegacy.get(id)),
  ).length;
  /** 보호자 문구 — 웹에서 내린 장면은 `guardianLines` 가 스스로 뺀다(check:owner 와 같은 셈) */
  const guardian = manifest.status === "fulfilled" ? guardianLines(assets) : undefined;
  const landing = siteText.status === "fulfilled" ? landingAgeAndTagline(siteText.value) : undefined;
  // 🙈 웹에서 내린 자리는 **채워진 것으로 세지 않는다** — 아이 화면에는 없기 때문이다.
  const missingPoster = sceneIds.filter((id) => !isOnWeb(assets[arPosterKey(id)])).length;

  return {
    partial,
    input: {
      goodsTotal: products.length,
      // 📢 소식 0건이면 [마을 이야기]가 통째로 비어 보인다(2026-09-05 운영 실측).
      // 🔴 **못 읽었으면 0 이 아니라 undefined** — 「없다」와 「못 물어봤다」를 뭉개면
      //    소식이 멀쩡히 있는데도 빨갛게 뜬다.
      noticeCount: notices.status === "fulfilled" ? notices.value.notices.length : undefined,
      goodsMissingStore: products.filter((p) => storeLinks(p).length === 0).length,
      // 🐱 AI 도슨트 — 검수 대기가 쌓여 있으면 아무도 모른 채 잠든다.
      qaPending: agentDoc.agents.reduce(
        (n, a) => n + a.qa.filter((q) => q.status === "pending").length,
        0,
      ),
      // 1단계 이상인데 승인된 답이 하나도 없는 친구(2단계는 자유 대화가 본체라 제외)
      agentsEmpty: agentDoc.agents.filter(
        (a) => a.level === 1 && !a.qa.some((q) => q.status === "approved"),
      ).length,
      // 고른 묶음에 글이 하나도 없는 친구 — 관리자에선 체크가 들어가 있어 다 된 것처럼 보인다.
      agentsEmptyPacks: agentDoc.agents.filter(
        (a) =>
          a.level >= 1 &&
          a.packs.length > 0 &&
          !a.packs.some((id) => (kbDoc.packs.find((k) => k.id === id)?.docs.length ?? 0) > 0),
      ).length,
      // 📚📚 같은 이름의 책이 나란히 선 경우 — 반씩 들이면 생긴다(판정은 shared 한 곳)
      dupBookTitles:
        books.status === "fulfilled" ? duplicateBookTitles(books.value.list).length : 0,
      // 🎙️ 자막은 있는데 소리가 없는 쪽 — 판정은 shared 한 곳(관리자 화면·pnpm book:audio 와 같은 규칙)
      dubMissing:
        books.status === "fulfilled"
          ? books.value.docs.reduce((n, d) => {
              if (!d) return n;
              // 🔁 「어떤 글로 만들었나」도 넘긴다 — 자막이 바뀐 소리를 「다 됐다」로 세지 않게(읽어주기 화면과 같은 규칙)
              const rows = dubRows(d.id, d.doc.pages as { id: string }[], (k) => isOnWeb(assets[k]), (k) => assets[k]?.fromText);
              return n + dubCount(rows, "ko").missing;
            }, 0)
          : 0,
      /**
       * 🪄 **재료는 올렸는데 타깃이 없는 쪽.** 그 쪽은 아이 화면에 [AR로 보기] 가 아예 안 뜬다 —
       * 올린 영상·그림이 통째로 잠든다. 판정은 매니페스트 키만 보면 된다(타깃이 있으면 안 센다).
       */
      /**
       * 마법을 반만 만든 쪽 — 판정은 shared arInteractIssues 한 곳(편집기와 같은 말).
       * 매니페스트를 못 읽었으면 세지 않는다: 없다 와 못 물어봤다 는 다른 말이다.
       */
      magicHalfDone:
        manifest.status === "fulfilled"
          ? Object.keys(assets).filter((k) => {
              const def = (assets[k] as { interact?: unknown } | undefined)?.interact;
              return !!def && arInteractIssues(def).length > 0;
            }).length
          : undefined,
      arNoTarget: (() => {
        const ids = new Set<string>();
        for (const k of Object.keys(assets)) {
          const m = /^ar.(video|overlay|poster|spine).(.+?)(.json|.atlas|.tex..*)?$/.exec(k);
          if (m && isOnWeb(assets[k])) ids.add(m[2]!);
        }
        let n = 0;
        for (const id of ids) if (!isOnWeb(assets[arTargetKey(id)])) n++;
        return n;
      })(),
      /**
       * 👻 **책에서 사라진 쪽에 걸린 AR 타깃.** 책은 살아 있는데 그 **쪽만** 없다 —
       * 쪽을 지우거나 쪽 id 를 바꾸면 타깃이 그대로 남는다.
       *
       * 🔴 아이 화면에 그대로 나간다: AR 목록에 「p07」, 체험 화면에 「p07를 비춰보세요」.
       * 쪽 번호는 책 차례에서 오는데(2026-09-03) 그 쪽이 책에 없으면 지어내지 않기 때문이다.
       *
       * 🔴 **책 목록·본문을 못 받았으면 세지 않는다** — 못 받은 것을 「쪽이 없다」로 읽으면
       * 멀쩡한 AR 을 전부 유령으로 신고한다(`bookOrphanAssets` 와 같은 조심).
       */
      arPageGone:
        books.status === "fulfilled"
          ? (() => {
              /**
               * 본문을 **받은** 책만 넣는다. 못 받은 책은 「쪽이 없다」고 말할 수 없다.
               *
               * 🔴 **쪽이 0개인 책도 넣지 않는다.** `loadBookDoc` 은 못 읽어도 예외 대신
               * `{ pages: [] }` 를 돌려준다 — 「빈 책」과 「못 읽었다」가 **같은 모양**이다.
               * 그대로 세면 엣지가 잠깐 흔들린 날 그 책의 AR 이 통째로 「사라진 쪽」으로
               * 신고된다. 진짜 빈 책이라면 걸린 AR 도 없으니 안 세도 잃는 게 없다.
               */
              const pagesOf = new Map<string, string[]>();
              for (const d of books.value.docs) {
                const ps = (d?.doc?.pages as { id?: string }[] | undefined) ?? [];
                if (ps.length === 0) continue;
                pagesOf.set(d!.id, ps.map((p) => String(p.id ?? "")));
              }
              // 세는 규칙은 shared 한 곳(`arPagesGone`) — 화면에서 또 짜면 웹과 갈린다
              const onWeb = Object.keys(assets)
                .filter((k) => k.startsWith("ar.target.") && isOnWeb(assets[k]))
                .map((k) => k.slice("ar.target.".length));
              return arPagesGone(onWeb, (id) => pagesOf.get(id)).length;
            })()
          : 0,
      /**
       * 📚 **어느 책에도 안 붙은 그림·소리.** AR 의 `arNoTarget` 과 같은 종류다 —
       * 재료는 올렸는데 그걸 쓰는 자리가 없다.
       *
       * 책을 만들다 말면(쪽은 올렸는데 책을 저장 안 했거나 나중에 책만 지웠거나)
       * 파일은 그대로 남는데 **아이 화면에는 그 책이 아예 없다.** 관리자에도 아무 말이 없어
       * 운영자는 「분명히 올렸는데」만 겪는다(2026-08-28 실제로 31개가 그 상태였다).
       *
       * 🔴 **목록을 못 받았으면 세지 않는다** — 못 받은 것을 「책이 없다」로 읽으면
       * 멀쩡한 책의 파일을 전부 고아로 신고하게 된다.
       *
       * 🔴 그런데 `status` 만으로는 그 약속을 **못 지킨다**(2026-09-03에 알았다).
       * `listBooks()` 는 엣지도 저장소도 못 읽으면 **예외 대신 빈 목록**을 돌려준다 —
       * 그래서 `fulfilled` 인데 `[]` 다. 그대로 세면 책 그림·소리가 **전부** 고아가 된다.
       * 책이 한 권도 없으면 어차피 책 파일도 없어야 하니, **빈 목록이면 말하지 않는다.**
       */
      bookOrphanAssets:
        books.status === "fulfilled" && books.value.list.length > 0
          ? (() => {
              // 키에서 책 이름을 읽는 판정은 **키를 만드는 곳**(shared/bookMeta)에 있다 —
              // 여기서 정규식을 또 쓰면 옛 형식(책 이름 없는 음성 키)에서 갈린다
              const alive = new Set(books.value.list.map((b) => b.id));
              let n = 0;
              for (const k of Object.keys(assets)) {
                const id = bookIdOfAssetKey(k);
                if (id && !alive.has(id)) n++;
              }
              return n;
            })()
          : 0,
      /**
       * 🛍️ **어느 상품 것도 아닌 사진.** 상품을 목록에서 지워도 사진은 매니페스트에 남는다 —
       * 책에서 겪은 것과 같은 종류다(지운 책의 파일 31개). 남는 것 자체는 괜찮지만
       * **모르면** 용량만 먹고 아무도 못 치운다.
       *
       * 🔴 목록을 못 받았으면 세지 않는다 — 못 받은 것을 「상품이 없다」로 읽으면
       * 멀쩡한 사진을 전부 고아로 신고한다.
       *
       * 🔴 `loadGoodsDoc()` 도 못 읽으면 **예외 대신 `{ products: [] }`** 를 준다(책과 같다).
       * `fulfilled` 인데 비어 있을 수 있으니 **빈 목록이면 말하지 않는다.**
       */
      goodsOrphanImages:
        goods.status === "fulfilled" && goods.value.products.length > 0
          ? (() => {
              const alive = new Set(goods.value.products.map((p) => p.id));
              let n = 0;
              for (const k of Object.keys(assets)) {
                const id = goodsIdOfSlot(k);
                if (id && !alive.has(id)) n++;
              }
              return n;
            })()
          : 0,
      /**
       * 🔴 **아이 화면 목록에 아예 안 뜨는 카드.** 판정은 아이 화면과 같은 함수(`arSceneReady`).
       * 관리자가 스스로 재면 「보인다고 해 놓고 안 보이는」 상태가 생긴다.
       */
      arHidden: sceneIds.filter(
        (id) =>
          !arSceneReady({
            id,
            title_ko: "",
            targetSrc: assets[arTargetKey(id)]?.url,
            fallback_youtube: assets[arTargetKey(id)]?.fallbackYoutube,
          }),
      ).length,
      arMissingVideo: missingVideo,
      arMissingPoster: missingPoster,
      // 아이 화면에 실제로 나가는 행사만 센다(스위치를 켠 것) — shared 와 같은 규칙
      visibleEvents:
        events.status === "fulfilled" ? events.value.filter((e) => e.visible !== false).length : 0,
      /**
       * 🎥 **아직 Jitsi 로만 열리는 행사** — 통화가 5분 뒤 끊긴다. 방 이름이 위험한가와
       * 다른 문제다(이름이 무엇이든 끊긴다). 판정은 shared 한 곳(`stageJitsiOnly`).
       */
      /**
       * 🔴 **기본 회의 주소를 같이 본다.** 안 보면 운영자가 기본 방을 넣어도
       * 여기는 계속 「화상이 아직 안 열려요」라고 말한다 — 아이 화면은 이미 그 방으로
       * 열리는데 관리자만 딴소리를 하는 것이고, 그러면 목록 전체를 안 믿게 된다.
       */
      eventJitsiOnly:
        events.status === "fulfilled"
          ? events.value.filter((e) => stageJitsiOnly(e.stage, siteMeet)).length
          : 0,
      eventJitsiOnlyWhere:
        events.status === "fulfilled"
          ? events.value.filter((e) => stageJitsiOnly(e.stage, siteMeet)).map((e) => e.title)
          : [],
      /**
       * 🧪 **아이 화면에 「테스트」 행사가 떠 있나.** 판정은 shared 한 곳
       * (`testEventVisible`) — 8월에 데모 행사가 「지금 열리고 있어요」로 운영에 떠
       * 있었고, 그때 만든 `visible` 스위치는 **켜 두는 것을 막지 못한다.**
       */
      eventTestVisible:
        events.status === "fulfilled" ? events.value.filter(testEventVisible).length : 0,
      eventTestVisibleWhere:
        events.status === "fulfilled"
          ? events.value.filter(testEventVisible).map((e) => e.title)
          : [],
      kidsModePublished: ui.status === "fulfilled" ? kidsModePublished(ui.value) : undefined,
      guardianDone: guardian?.done,
      guardianTotal: guardian ? guardian.done + guardian.missing.length : undefined,
      landingBands: landing?.bands,
      landingTagline: landing?.tagline,
      sideDoor: lobby.status === "fulfilled" ? hasEventSideDoor(lobby.value) : undefined,
      hasRallyForm: rally.status === "fulfilled" && !!rally.value.event?.claimFormUrl,
      rallyPoints: rally.status === "fulfilled" ? (rally.value.points?.length ?? 0) : 0,
      hasContactUrl: notices.status === "fulfilled" && !!notices.value.contactUrl,
      // 판정은 shared 한 곳(`brokenLinks`) — 고치는 화면([🗺️ 체험공간 배치])도 같은 걸 본다
      worldLinksMissing:
        maps.status === "fulfilled"
          ? maps.value.reduce<number>((n, m) => n + brokenLinks(m).length, 0)
          : 0,
      worldLinksWhere:
        maps.status === "fulfilled"
          ? maps.value.flatMap((m, zi) =>
              brokenLinks(m).map((o) => `${zoneSay(ZONES[zi] ?? "")} · ${o.id}`),
            )
          : [],
      /**
       * 🧱 벽에 갇힌 문·친구·링크. 판정은 **shared 한 곳**(`stuckObjects`) — 지도 검사와
       * 같은 것을 본다. 여기서 다시 짜면 「관리자에선 괜찮다는데 아이는 못 간다」가 된다.
       */
      worldStuck:
        maps.status === "fulfilled"
          ? maps.value.reduce<number>((n, m) => n + stuckObjects(m).length, 0)
          : 0,
      /**
       * 🔴 **어느 구역의 무엇인지까지 준다.** 개수만 주면 운영자는 다섯 화면을 열어
       * 눈으로 하나씩 찾아야 한다 — 그래서 안 고치고 며칠씩 남는다(실측).
       * `ZONES` 와 `maps.value` 는 **같은 차례**다(위에서 `ZONES.map` 으로 받았다).
       */
      worldStuckWhere:
        maps.status === "fulfilled"
          ? maps.value.flatMap((m, zi) =>
              stuckObjects(m).map((o) => `${zoneSay(ZONES[zi] ?? "")} · ${o.id}`),
            )
          : [],
    },
  };
}

/**
 * 🗓 기한 딱지 — 「9일 지남」·「D-3」. 날짜 판정은 shared(`dueSay`) 한 곳 — `pnpm check:owner` 와 같은 말.
 * 지난 것은 빨갛게, 사흘 안은 노랗게.
 */
function DueTag({ due }: { due: string }) {
  const today = new Date();
  const n = daysUntil(due, today);
  const tone = n < 0 ? "late" : n <= 3 ? "soon" : "later";
  return (
    <span className={`ready__due ready__due--${tone}`} data-due={due}>
      {dueSay(due, today)}
    </span>
  );
}
